import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import RoundButton from 'Components/UI/RoundButton';
import {
  householdBlueprintUri,
  householdPlansUri,
  householdSecureFilesUri,
  householdTimelineUri,
  householdEditRecordUri,
  workspacesUri,
  taskBoardUri,
  householdSnapshotUri,
  advisorMessagesUri,
} from 'Consts/advisorUris';
import relationships from 'Consts/Relationships';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import useUserContext from 'Context/UserContext';
import { addSpacesAndCapitalizeAllWords } from 'Helpers/Formats';
import featureEnabled from 'Helpers/Affordances';
import { navigateTo } from 'Helpers/Redirect';
import fr from 'facet-rest';
import { advisorPageMessages } from 'Consts/Strings';
import { buildPageLink } from 'Views/Page/Content/Templates';
import { missionControlURL } from 'Hooks/useMissionControlURL';
import { balanceSheetPageURL } from 'Hooks/useBalanceSheetPageURL';

const styles = theme => ({
  root: {
    margin: theme.spacing(0.5, 0),
    fontWeight: theme.typography.fontWeightBold,
    width: theme.dimensions.xs,
  },
});

const tabToUriMap = {
  accounts: advisorMessagesUri, // Doesn't matter, it gets overwritten later
  actionItems: taskBoardUri,
  blueprint: householdBlueprintUri,
  missionControl: advisorMessagesUri, // Doesn't matter, it gets overwritten later
  plans: householdPlansUri,
  questionnaire: householdEditRecordUri,
  secureFiles: householdSecureFilesUri,
  timeline: householdTimelineUri,
  snapshot: householdSnapshotUri,
  messages: advisorMessagesUri,
};


const createHouseholdUriFromTab = (tab, householdId, link) => {
  const uriFunction = tabToUriMap[tab];
  if (!uriFunction) {
    throw new Error(`Could not generate a URI from the given tab: ${tab}`);
  }

  return uriFunction(tab === 'actionItems' ? { householdId, link } : householdId);
};

const LaunchWorkspaceButton = ({
  className,
  classes,
}) => {
  const { householdId, householdResource } = useSimpleHousehold();
  const { userResource, isAgent } = useUserContext();
  const [tabs, setTabs] = useState([]);
  const [isWorkspaceConfigured, setIsWorkspaceConfigured] = useState(false);

  const { logEvent } = useEventTrackingLogEvent();

  const handleLaunchWorkspace = () => {
    if (!isWorkspaceConfigured) {
      navigateTo(workspacesUri);
      return;
    }

    tabs.forEach((tab) => {
      let householdUri = createHouseholdUriFromTab(tab, householdId, householdResource?.relationship(relationships.simpleActionItems)?.link('related')?.url());
      if (tab === 'messages') {
        householdUri = householdResource.withRelationship(fr.relationships.messagesPage.name, (rel) => {
          return buildPageLink(
            rel.link('related'),
            advisorPageMessages,
            {}, // params
          );
        }, () => {
          return '';
        });
      }
      if (tab === 'missionControl') {
        householdUri = missionControlURL(householdResource, householdId);
      }
      if (tab === 'accounts') {
        householdUri = balanceSheetPageURL(householdResource, householdId, isAgent);
      }

      const win = window.open(householdUri, '_blank');
      if (win) {
        setTimeout(() => win.document.title = addSpacesAndCapitalizeAllWords(tab), 500); // eslint-disable-line
      }
    });
  };

  useEffect(() => {
    userResource // eslint-disable-line
      ?.relationship(relationships.workspaces)
      ?.link('related')
      .Get()
      .then((coll) => {
        const workspace = coll.resources().head();
        if (!workspace) return;
        setTabs(workspace.attr('tabs'));
      });
  }, [userResource]);

  useEffect(() => {
    setIsWorkspaceConfigured(featureEnabled(userResource, relationships.workspaces) && tabs.length > 0);
  }, [tabs]);

  const buttonLabel = `${isWorkspaceConfigured ? 'Launch' : 'Configure'} Workspace`;

  return (
    <RoundButton
      className={classNames(classes.root, className)}
      color="secondary"
      padding="squished"
      disableElevation
      fullWidth
      variant="contained"
      onClick={() => {
        logEvent(`Clicked ${buttonLabel}`);
        handleLaunchWorkspace();
      }}
    >
      {buttonLabel}
    </RoundButton>
  );
};

LaunchWorkspaceButton.defaultProps = {
  className: '',
};

LaunchWorkspaceButton.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(LaunchWorkspaceButton);
